import { encodeDateCellValue } from '../date-format.js';
import {
  encodeCellValue,
  encodeMultiSelectCellValue,
  encodeSelectCellValue,
  parseMultiSelectPlainText,
  type PropertyValuePayload,
} from '../property-value.js';
import {
  configWithSelectOptions,
  ensureOption,
  normalizeSelectOptions,
  resolveOptionByValue,
  type SelectOption,
} from '../select-options.js';
import type { DatabaseColumnDefinition, DatabaseColumnType } from '../schema.js';

import { isImportableColumnType } from './infer-column-type.js';
import { parseImportDateRange, parseImportNumber } from './parse-import-values.js';
import type {
  BuildIngestPlanResult,
  DatabaseIngestPlan,
  ImportColumnMappingChoice,
  ImportMappingInput,
  ImportTable,
} from './types.js';

function newColumnId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `col-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function columnNameKey(name: string): string {
  return name.trim().toLowerCase();
}

export function defaultImportMapping(
  table: ImportTable,
  existingColumns: DatabaseColumnDefinition[],
): ImportMappingInput {
  const byName = new Map(
    existingColumns.map((c) => [columnNameKey(c.name), c.id] as const),
  );
  const titleSource =
    table.columns.length > 1 ? (table.columns[0]?.sourceName ?? null) : null;
  const columns: ImportColumnMappingChoice[] = table.columns.map((col) => {
    if (titleSource && col.sourceName === titleSource) {
      return {
        sourceName: col.sourceName,
        target: 'skip' as const,
        finalType: col.inferredType,
      };
    }
    const existingId = byName.get(columnNameKey(col.sourceName));
    const existing = existingId
      ? existingColumns.find((c) => c.id === existingId)
      : undefined;
    if (
      existing &&
      (existing.type === 'relation' || existing.type === 'rollup')
    ) {
      return {
        sourceName: col.sourceName,
        target: 'new' as const,
        finalType: col.inferredType,
      };
    }
    const finalType =
      existing &&
      isImportableColumnType(existing.type as string) &&
      existing.type !== 'relation' &&
      existing.type !== 'rollup'
        ? (existing.type as DatabaseColumnType)
        : col.inferredType;
    return {
      sourceName: col.sourceName,
      target: existingId ?? 'new',
      finalType,
    };
  });
  return { titleSourceName: titleSource, columns };
}

type ResolvedColumn = {
  sourceName: string;
  columnId: string;
  name: string;
  type: DatabaseColumnType;
  config: Record<string, unknown>;
  isNew: boolean;
};

type SelectOptionAcc = { config: Record<string, unknown>; created: number };

function encodeImportCell(
  raw: string,
  type: DatabaseColumnType,
  selectAcc: SelectOptionAcc | null,
): PropertyValuePayload | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  if (type === 'select' || type === 'multi_select') {
    if (!selectAcc) {
      return null;
    }
    let workingConfig = selectAcc.config;

    if (type === 'select') {
      const whole = resolveOptionByValue(workingConfig, trimmed);
      if (!whole) {
        const result = ensureOption(workingConfig, trimmed);
        workingConfig = result.config;
        if (result.created) {
          selectAcc.created++;
        }
        selectAcc.config = workingConfig;
        return encodeSelectCellValue(result.option.id);
      }
      return encodeSelectCellValue(whole.id);
    }

    const labels = parseMultiSelectPlainText(trimmed);
    const ids: string[] = [];
    for (const label of labels) {
      const existing = resolveOptionByValue(workingConfig, label);
      if (existing) {
        ids.push(existing.id);
      } else {
        const result = ensureOption(workingConfig, label);
        workingConfig = result.config;
        if (result.created) {
          selectAcc.created++;
        }
        ids.push(result.option.id);
      }
    }
    selectAcc.config = workingConfig;
    return encodeMultiSelectCellValue(ids);
  }

  if (type === 'number') {
    const n = parseImportNumber(trimmed);
    if (n == null) {
      return null;
    }
    return encodeCellValue('number', n);
  }

  if (type === 'date') {
    const parsed = parseImportDateRange(trimmed);
    if (!parsed) {
      return encodeCellValue('date', trimmed);
    }
    return encodeDateCellValue(parsed.start, parsed.end);
  }

  return encodeCellValue(type, trimmed);
}

export function buildIngestPlan(
  table: ImportTable,
  existingColumns: DatabaseColumnDefinition[],
  mapping: ImportMappingInput,
): BuildIngestPlanResult {
  const existingById = new Map(existingColumns.map((c) => [c.id, c]));
  const existingByName = new Map(
    existingColumns.map((c) => [columnNameKey(c.name), c] as const),
  );
  const sourceByName = new Map(table.columns.map((c) => [c.sourceName, c]));

  const optionAccumulator = new Map<string, SelectOptionAcc>();
  const resolved: ResolvedColumn[] = [];
  const newColumns: DatabaseColumnDefinition[] = [];

  for (const choice of mapping.columns) {
    const source = sourceByName.get(choice.sourceName);
    if (!source) {
      continue;
    }
    if (
      mapping.titleSourceName &&
      choice.sourceName === mapping.titleSourceName
    ) {
      continue;
    }
    if (choice.target === 'skip') {
      continue;
    }

    const finalTypeRaw = choice.finalType ?? source.inferredType;
    const finalType: DatabaseColumnType = isImportableColumnType(finalTypeRaw)
      ? finalTypeRaw
      : 'text';

    if (choice.target === 'new') {
      const id = newColumnId();
      const def: DatabaseColumnDefinition = {
        id,
        name: source.sourceName,
        type: finalType,
        config:
          finalType === 'select' || finalType === 'multi_select'
            ? { options: [] as SelectOption[] }
            : undefined,
      };
      newColumns.push(def);
      resolved.push({
        sourceName: source.sourceName,
        columnId: id,
        name: def.name,
        type: finalType,
        config: def.config ?? {},
        isNew: true,
      });
      continue;
    }

    const existing =
      existingById.get(choice.target) ?? existingByName.get(columnNameKey(choice.sourceName));
    if (!existing) {
      const id = newColumnId();
      const def: DatabaseColumnDefinition = {
        id,
        name: source.sourceName,
        type: finalType,
        config:
          finalType === 'select' || finalType === 'multi_select'
            ? { options: [] as SelectOption[] }
            : undefined,
      };
      newColumns.push(def);
      resolved.push({
        sourceName: source.sourceName,
        columnId: id,
        name: def.name,
        type: finalType,
        config: def.config ?? {},
        isNew: true,
      });
      continue;
    }

    const useType = isImportableColumnType(existing.type as string)
      ? (existing.type as DatabaseColumnType)
      : finalType;
    const effectiveType =
      existing.type === 'relation' || existing.type === 'rollup' ? finalType : useType;

    resolved.push({
      sourceName: source.sourceName,
      columnId: existing.id,
      name: existing.name,
      type: effectiveType,
      config: { ...(existing.config ?? {}) },
      isNew: false,
    });
  }

  const columnsAfter: DatabaseColumnDefinition[] = [
    ...existingColumns,
    ...newColumns.map((c) => {
      const r = resolved.find((x) => x.columnId === c.id && x.isNew);
      if (r && (r.type === 'select' || r.type === 'multi_select')) {
        return { ...c, config: r.config };
      }
      return c;
    }),
  ];

  const rows: DatabaseIngestPlan['rows'] = table.rows.map((row, index) => {
    let title: string | null = null;
    if (mapping.titleSourceName) {
      const raw = row.cells[mapping.titleSourceName];
      if (raw != null && String(raw).trim()) {
        title = String(raw).trim();
      }
    }

    const properties: Record<string, Record<string, unknown>> = {};

    for (const col of resolved) {
      if (mapping.titleSourceName === col.sourceName) {
        continue;
      }
      const raw = row.cells[col.sourceName];
      if (raw == null || String(raw).trim() === '') {
        continue;
      }

      let selectAcc: SelectOptionAcc | null = null;
      if (col.type === 'select' || col.type === 'multi_select') {
        let acc = optionAccumulator.get(col.columnId);
        if (!acc) {
          acc = { config: { ...col.config }, created: 0 };
          optionAccumulator.set(col.columnId, acc);
        }
        selectAcc = acc;
      }

      const payload = encodeImportCell(String(raw), col.type, selectAcc);
      if (payload) {
        if (selectAcc) {
          col.config = selectAcc.config;
          const colIdx = columnsAfter.findIndex((c) => c.id === col.columnId);
          if (colIdx >= 0) {
            columnsAfter[colIdx] = {
              ...columnsAfter[colIdx]!,
              config: selectAcc.config,
            };
          }
        }
        properties[col.columnId] = payload as Record<string, unknown>;
      }
    }

    return { title, position: index, properties };
  });

  let optionsCreated = 0;
  for (const acc of optionAccumulator.values()) {
    optionsCreated += acc.created;
  }

  for (const r of resolved) {
    if (r.type === 'select' || r.type === 'multi_select') {
      const acc = optionAccumulator.get(r.columnId);
      const config = acc?.config ?? r.config;
      const idx = columnsAfter.findIndex((c) => c.id === r.columnId);
      if (idx >= 0) {
        columnsAfter[idx] = {
          ...columnsAfter[idx]!,
          config: configWithSelectOptions(config, normalizeSelectOptions(config)),
        };
      }
    }
  }

  const plan: DatabaseIngestPlan = {
    newColumns: newColumns.map((c) => {
      const updated = columnsAfter.find((x) => x.id === c.id);
      return updated ?? c;
    }),
    columns: columnsAfter,
    rows,
  };

  return { plan, optionsCreated };
}
